import React, { useCallback } from 'react';
import { Button, useNoticeBannersStackContext } from '@sweetgreen/garnish';

import { ApplePay } from '@order/components';
import { APPLE_PAY } from '@order/constants';
import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';
import { convertPriceToFloat } from '@order/utils';

import { type useCheckoutContext } from '../../CheckoutProvider';

// ────────────────────────────────────────────────────────────────────────────

export const CheckoutPlaceOrderButton = (props: CheckoutOrderBlockProps) => {
  const {
    payload,
    orderId,
    total,
    isSubmittingOrder,
    cannotSubmitOrderReason,
    shouldBlockCheckoutOnPress,
    isApplePaySupported,
    handleInvalidWantedTime,
    validateCheckoutFields,
    submitOrder,
  } = props;

  // ─── Context ──────────────────────────────────────────────────────────────

  const { t } = useLocalizationContext();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const { track } = useTelemetry();

  // ─── Validation ───────────────────────────────────────────────────────────

  const validateCanPlaceOrder = useCallback(() => {
    validateCheckoutFields(payload);

    const shouldShowError = !orderId || shouldBlockCheckoutOnPress;

    if (shouldShowError) {
      addNoticeBanner({ text: cannotSubmitOrderReason, palette: 'caution' });

      return false;
    }

    return true;
  }, [
    payload,
    cannotSubmitOrderReason,
    orderId,
    shouldBlockCheckoutOnPress,
    validateCheckoutFields,
    addNoticeBanner,
  ]);

  // ─── Submit Order ─────────────────────────────────────────────────────────

  const submitOrderCallback = useCallback(async () => {
    if (orderId && validateCanPlaceOrder()) {
      await submitOrder({
        input: {
          ...payload,
          orderId,
        },
        handleInvalidWantedTime,
      });
    }
  }, [
    orderId,
    payload,
    handleInvalidWantedTime,
    submitOrder,
    validateCanPlaceOrder,
  ]);

  const submitOrderWithApplePay = useCallback(
    async (paymentMethodId: string) => {
      if (orderId && validateCanPlaceOrder()) {
        track('checkout.apple-pay', {
          ...payload,
          orderValue: convertPriceToFloat(total),
        });

        await submitOrder({
          input: {
            ...payload,
            paymentMethodId,
            paymentMethodType: 'PaymentMethodCard',
            orderId,
          },
          handleInvalidWantedTime,
        });
      }
    },
    [
      orderId,
      total,
      payload,
      track,
      validateCanPlaceOrder,
      handleInvalidWantedTime,
      submitOrder,
    ],
  );

  // ─── Apple Pay ────────────────────────────────────────────────────────────

  if (isApplePaySupported && isCheckoutWithApplePay(props)) {
    return (
      <ApplePay.Checkout
        amountToCapture={total}
        isSubmitting={isSubmittingOrder}
        validateCanPlaceOrder={validateCanPlaceOrder}
        onCheckout={submitOrderWithApplePay}
      />
    );
  }

  // ─── Regular ──────────────────────────────────────────────────────────────

  return (
    <Button
      onPress={submitOrderCallback}
      isLoading={isSubmittingOrder}
      accessibilityLabel={t('checkout.place-order')}
      size="large"
      palette="primary"
    >
      {t('checkout.place-order')}
    </Button>
  );
};

// ─── Helpers ────────────────────────────────────────────────────────────────

function isCheckoutWithApplePay(props: CheckoutOrderBlockProps) {
  const { payload, paymentMethods } = props;

  return (
    payload.paymentMethodId !== 'ADD' &&
    (payload.paymentMethodId === APPLE_PAY ||
      !paymentMethods.some((pm) => pm.id === payload.paymentMethodId))
  );
}

// ─── Types ──────────────────────────────────────────────────────────────────

type CheckoutOrderBlockProps = Pick<
  ReturnType<typeof useCheckoutContext>,
  | 'payload'
  | 'orderId'
  | 'checkoutCredit'
  | 'paymentMethods'
  | 'isSubmittingOrder'
  | 'cannotSubmitOrderReason'
  | 'shouldBlockCheckoutOnPress'
  | 'handleInvalidWantedTime'
  | 'validateCheckoutFields'
  | 'isApplePaySupported'
  | 'submitOrder'
  | 'total'
>;
